import type { QueryClient } from '@tanstack/react-query';
import type { PricelistCaseResponse } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import type { PricelistCaseVendor } from './pricelistCaseVendors';

/**
 * The exact key the case page reads (`get.queryOptions`). Not `get.key(...)`:
 * that is a partial key without the query type, so writing to it creates a
 * second cache entry the page never reads, and the page only caught up on
 * the next refetch — the ~1s lag after saving vendors (GEN-3074).
 */
export function pricelistCaseQueryKey(caseId: string) {
  return orpc.inventoryManagement.pricelistCases.get.queryKey({
    input: { caseId },
  });
}

export function rememberPricelistCase(
  queryClient: QueryClient,
  updated: PricelistCaseResponse,
): void {
  queryClient.setQueryData(pricelistCaseQueryKey(updated.id), updated);
}

/** The case as it will read once a vendor-set save lands (optimistic). */
export function withPricelistCaseVendors(
  pricelistCase: PricelistCaseResponse,
  vendors: readonly PricelistCaseVendor[],
): PricelistCaseResponse {
  const [primary] = vendors;
  if (primary === undefined) return pricelistCase;
  return {
    ...pricelistCase,
    companyId: primary.companyId,
    companyName: primary.companyName,
    vendors: vendors.map((vendor) => ({
      companyId: vendor.companyId,
      companyName: vendor.companyName,
      vendorNumbers: [...(vendor.vendorNumbers ?? [])],
    })),
  };
}

const PRICELIST_CASE_VIEW_KEYS = [
  () => orpc.inventoryManagement.pricelistCases.get.key(),
  () => orpc.inventoryManagement.pricelistCases.list.key(),
  () => orpc.inventoryManagement.pricelistCases.timeline.key(),
] as const;

export function refreshPricelistCaseViews(queryClient: QueryClient): void {
  for (const keyOf of PRICELIST_CASE_VIEW_KEYS) {
    void queryClient.invalidateQueries({ queryKey: keyOf() });
  }
}
